import { eventBus } from '@/instance';
import { reactive, watch } from 'vue'
import { filesRepository } from '@/data'
import { AnyService, AssetService, BatchService, UploadService } from '@/services'
import { Asset, NeedUploadAsset, FileStatus } from '@/types'
import { EventType } from '@/constants/event';
import { formatSize, getGuid } from '@/utils/utils';
import { useI18n } from './i18n';

const MAX_TASK_COUNT = 6

const SEGMENT_SIZE = 2 * 1024 * 1024

export type UploadComposition = {
  scheduleUpload: () => void
  handleUploadFiles: (files: FileList | File[], folder: Asset) => void
  handleUploadFolders: (files: FileList | File[], folder: Asset) => void
  handlePause: (file: NeedUploadAssetUploader) => void
  handleResume: (file: NeedUploadAssetUploader) => void
  handleCancel: (file: NeedUploadAssetUploader) => void
  uploadingFiles: NeedUploadAssetUploader[]
  queuedFiles: AssetUploader[]
  uploadedFiles: NeedUploadAssetUploader[]
  resetUpload: () => void
}

export type NeedUploadAssetUploader = AssetUploader & NeedUploadAsset

export type AssetUploader = {
  parent_asset_id: string
  percentage: number
  state: FileStatus
  speed: string
  name: string
  id: string
} & Uploader

type FolderModel = {
  name: string
  target: Asset
  files: Array<File>
  children: Array<FolderModel>
}

type Task = {
  id: string
  percentage: number
  index: number
  total: number
  url: string
}

type Uploader = {
  file: File
  tasks: Task[]
}

const needUpload = (i: AssetUploader) =>
  i.state === FileStatus.Queued || i.state === FileStatus.Uploading

// 把数据共享给每次调用useUpload
const uploadingFiles: NeedUploadAssetUploader[] = reactive([])
const queuedFiles: AssetUploader[] = reactive([])
const uploadedFiles: NeedUploadAssetUploader[] = reactive([])
const uploadingTasks: Task[] = []
let rafId = -1
export function useUpload (): UploadComposition {
  const { t } = useI18n()

  const fileToAsset = (file: File, parentAsset: Asset): AssetUploader => {
    return {
      file: file,
      tasks: [],
      parent_asset_id: parentAsset.id,
      percentage: 0,
      name: file.name,
      id: getGuid(),
      speed: t('upload.waiting'),
      state: FileStatus.Queued,
    }
  }

  // 先入库空素材，根据入库返回的路径来上载文件内容
  const scheduleUpload = async () => {
    clearTimeout(rafId)
    // 从queueFiles里面取file放到uploadingFiles
    if (
      queuedFiles.length &&
      uploadingFiles.filter(needUpload).length < MAX_TASK_COUNT
    ) {
      const file = queuedFiles.shift()!
      const newAsset = await AssetService.post<NeedUploadAsset>(
        `${file.parent_asset_id}/children`,
        {
          id: file.id,
          name: file.file.name,
          media: { size: file.file.size },
          fileType: file.file.type,
          type: 'asset',
        }
      )
      file.tasks = newAsset.upload_urls.map((i, index) => {
        return {
          id: newAsset.id,
          index: index,
          url: i,
          total: newAsset.upload_urls.length,
          percentage: 0,
        }
      })
      file.state = FileStatus.Uploading
      uploadingFiles.push(Object.assign(file, newAsset))
    }
    // 从uploadingFiles里面取分片任务到uploadingTasks然后执行上载
    const needUploadFiles = uploadingFiles.filter(needUpload)
    if (uploadingTasks.length < MAX_TASK_COUNT && needUploadFiles.length) {
      for (let i = 0; i < needUploadFiles.length; i++) {
        // 开始调度上载 先入库空素材，根据获取的uploadUrl 分任务
        const file = needUploadFiles[i]
        if (file.tasks.length) {
          const task = file.tasks.shift()!
          const blob = file.file.slice(
            task.index * SEGMENT_SIZE,
            Math.min(file.file.size, (task.index + 1) * SEGMENT_SIZE)
          )
          let timestamp = +new Date()
          let throttleIndex = 0
          AnyService.put(task.url, blob, {
            headers: {
              'content-type': '',
            },
            onUploadProgress: (e: ProgressEvent) => {
              const percentage = (e.loaded / e.total) * 100
              const totalPercentage = (percentage - task.percentage) / task.total
              const usedSecond = (+new Date() - timestamp) / 1000
              timestamp = +new Date()
              file.percentage! += totalPercentage
              // 降低速度变化的频率
              if (throttleIndex % 20 === 0) {
                file.speed = `${formatSize(totalPercentage * file.file.size / 100 / usedSecond)}/s`
              }
              throttleIndex++
              task.percentage = percentage
            },
          }).then(async (res) => {
            console.log(res)
            // 还需要处理上载未完成离开的时候删除空素材逻辑

            uploadingTasks.splice(uploadingTasks.indexOf(task), 1)
            if (
              file.tasks.length === 0 &&
              uploadingTasks.every((i) => i.id !== file.id)
            ) {
              await UploadService.post(`${file.upload_id}/completeupload`)
              file.state = FileStatus.Complete
              file.speed = t('upload.complete')
              uploadingFiles.removeBy((s, t) => s.id === t.id, file)
              uploadedFiles.push(file)
              if (!uploadingFiles.length && !queuedFiles.length) {
                clearTimeout(rafId) // 上载完成先停止调度
              }
            }
          }).catch((err: any) => {
            console.error(err)
            // 从uploading中移除加入file.tasks等待再次上载 暂不处理重试次数问题和请求中失败进度计算不准问题
            uploadingTasks.splice(uploadingTasks.indexOf(task), 1)
            file.tasks.push(task)
          })
          uploadingTasks.push(task)
          if (uploadingTasks.length >= MAX_TASK_COUNT) {
            break
          }
        }
      }
    }
    rafId = window.setTimeout(scheduleUpload, 50)
  }

  const handleUploadFiles = (files: FileList | File[], folder: Asset) => {
    const tempFiles: AssetUploader[] = []
    for (let i = 0; i < files.length; i++) {
      tempFiles.push(fileToAsset(files[i], folder))
    }
    if (!filesRepository[folder.id]) {
      filesRepository[folder.id] = []
    }
    filesRepository[folder.id].push(...tempFiles)
    queuedFiles.push(...tempFiles)
    eventBus.emit(EventType.FILE_UPLOAD, { type: 'add', key: folder.id, data: tempFiles })
  }

  const handleUploadFolders = async (
    files: FileList | File[],
    folder: Asset
  ) => {
    const folders: Array<FolderModel> = []
    const flatFolders: Array<FolderModel> = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const p = file.webkitRelativePath || file.relativePath || ''
      const index = p.lastIndexOf('/')
      const path = p.slice(0, index)
      let last = folders
      let temp = { target: folder } as FolderModel
      const paths = path.split('/')
      for (let n of paths) {
        const same = last.find((i) => i.name === n)
        if (same) {
          last = same.children
          temp = same
        } else {
          let newFolder = {
            name: n,
            target: await AssetService.post<Asset>(
              `${temp.target.id}/children`,
              {
                name: n,
                type: 'folder',
              }
            ),
            files: [],
            children: [],
          }
          last.push(newFolder)
          eventBus.emit(EventType.FILE_UPLOAD, {
            type: 'add',
            key: temp.target.id,
            data: [newFolder.target]
          })
          temp = newFolder
        }
      }
      !flatFolders.includes(temp) && flatFolders.push(temp)
      temp.files.push(file)
    }
    for (let i = 0; i < flatFolders.length; i++) {
      const folder = flatFolders[i]
      const tempFiles: AssetUploader[] = []
      folder.files.forEach((file) => {
        tempFiles.push(fileToAsset(file, folder.target))
      })
      if (!filesRepository[folder.target.id]) {
        filesRepository[folder.target.id] = []
      }
      folder.target.child_count = tempFiles.length
      filesRepository[folder.target.id].push(...tempFiles)
      queuedFiles.push(...tempFiles)
      eventBus.emit(EventType.FILE_UPLOAD, { type: 'add', key: folder.target.id, data: tempFiles })
    }
  }

  const handlePause = (file: NeedUploadAssetUploader) => {
    file.state = FileStatus.Paused
  }

  const handleResume = (file: NeedUploadAssetUploader) => {
    file.state = FileStatus.Uploading
  }

  const handleCancel = (file: NeedUploadAssetUploader) => {
    uploadingFiles.removeBy((s, t) => s.id === t.id, file)
    queuedFiles.removeBy((s, t) => s.id === t.id, file)
    filesRepository[file.parent_asset_id].removeBy((s, t) => s.id === t.id, file)
    // 彻底删除素材
    file.state !== FileStatus.Queued && BatchService.delete('upload_cancel', {
      data: {
        batch: [{ id: file.id, completely: true }],
      },
    })
    eventBus.emit(EventType.FILE_UPLOAD, { type: 'remove', key: file.parent_asset_id, data: [file] })
    // 暂时不做cancel执行中的请求
  }

  const resetUpload = () => {
    uploadedFiles.length = 0
    uploadingFiles.forEach(handleCancel)
    uploadingFiles.length = 0
    queuedFiles.length = 0
    uploadingTasks.length = 0
    for (let key in filesRepository) {
      delete filesRepository[key]
    }
  }
  watch([queuedFiles, uploadingFiles], () => {
    const hasTask = queuedFiles.length || uploadingFiles.length
    window.onbeforeunload = hasTask
      ? function (e) {
        return 'Some Task are Executing...'
      }
      : null
  })
  return {
    scheduleUpload,
    handleUploadFiles,
    handleUploadFolders,
    handlePause,
    handleResume,
    handleCancel,
    uploadingFiles,
    queuedFiles,
    uploadedFiles,
    resetUpload,
  }
}
